import { DateObject } from 'react-multi-date-picker'
import { calculateTotals, formatMoney } from '../../helpers/utils'
import { formatDateTimeFull } from '../../helpers/formatDate'
import {
  SETTLEMENT_DETAILS_APPROVAL_DATE_LABEL,
  SETTLEMENT_DETAILS_APPROVED_BY_LABEL,
  SETTLEMENT_DETAILS_BALANCED_TEXT,
  SETTLEMENT_DETAILS_ERP_NUMBER_LABEL,
  SETTLEMENT_DETAILS_DEPOSITS_DATE_PLACEHOLDER,
  SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE,
  SETTLEMENT_DETAILS_DEPOSITS_REQUIRED_FIELD_MESSAGE,
  SETTLEMENT_DETAILS_UNBALANCED_EXTRA_PREFIX,
  SETTLEMENT_DETAILS_UNBALANCED_EXTRA_SUFFIX,
  SETTLEMENT_DETAILS_UNBALANCED_MISSING_PREFIX,
  SETTLEMENT_DETAILS_UNBALANCED_MISSING_SUFFIX,
  SETTLEMENT_DETAILS_UNBALANCED_TEXT
} from '../../constants/settlementDetailsConstants'
import { DEPOSIT_NUMBER_MAX_LENGTH } from './settlementDetailsTabConstants'

export const createReceiptDraft = (row) => ({
  uid: row.uid || '',
  bank: row.bank && row.bank !== '-' ? row.bank : '',
  number: row.number && row.number !== '-' ? normalizeDepositNumber(row.number) : '',
  date: normalizeDateInputValue(row.date),
  amount: formatDepositAmountForDisplay(row.amount)
})

export const getBalanceStatusText = ({ balanced, totalToDeposit, totalDeposited }) => {
  if (balanced) return SETTLEMENT_DETAILS_BALANCED_TEXT

  const difference =
    Math.round((Number(totalToDeposit || 0) - Number(totalDeposited || 0)) * 100) / 100
  const absoluteDifference = Math.abs(difference)

  if (difference > 0)
    return `${SETTLEMENT_DETAILS_UNBALANCED_MISSING_PREFIX} ${formatMoney(absoluteDifference)} ${SETTLEMENT_DETAILS_UNBALANCED_MISSING_SUFFIX}`
  if (difference < 0)
    return `${SETTLEMENT_DETAILS_UNBALANCED_EXTRA_PREFIX} ${formatMoney(absoluteDifference)} ${SETTLEMENT_DETAILS_UNBALANCED_EXTRA_SUFFIX}`
  return SETTLEMENT_DETAILS_UNBALANCED_TEXT
}

export const getCashCountModel = (settlement) => {
  const cashCount = Array.isArray(settlement?.cash_count)
    ? settlement.cash_count[0]
    : settlement?.cash_count
  const denominations = cashCount?.denominations
  if (!denominations || Object.keys(denominations).length === 0) return null

  const rows = calculateTotals(denominations)
  const closureTotal = Number(
    cashCount?.closure_total ?? rows.reduce((sum, row) => sum + row.total, 0)
  )
  const reportedCash = Number(cashCount?.reported_cash ?? settlement?.cash_amount ?? 0)
  return {
    rows,
    reportedCash,
    closureTotal,
    cashDifference: Number(cashCount?.cash_difference ?? reportedCash - closureTotal)
  }
}

export const buildLiquidationDetailsFields = (settlement) => {
  const values = [
    settlement?.supervisor_email,
    settlement?.supervisor_reviewed_at,
    settlement?.erp_document_reference
  ]
  if (!values.some((value) => value !== null && value !== undefined && value !== '')) return []
  return [
    { label: SETTLEMENT_DETAILS_APPROVED_BY_LABEL, value: settlement?.supervisor_email || '-' },
    {
      label: SETTLEMENT_DETAILS_APPROVAL_DATE_LABEL,
      value: formatDateTimeFull(settlement?.supervisor_reviewed_at)
    },
    { label: SETTLEMENT_DETAILS_ERP_NUMBER_LABEL, value: settlement?.erp_document_reference || '-' }
  ]
}

export const getDepositUiChangeKey = (row) => String(row?.id ?? row?.uid ?? '')

export const getDepositUiChanges = (row, draftRow) => {
  const changes = {}
  const previousBank = row?.bank || SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
  const currentBank =
    String(draftRow?.bank || '').trim() || SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
  const previousNumber =
    normalizeDepositNumber(row?.number) || SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
  const currentNumber =
    normalizeDepositNumber(draftRow?.number) || SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
  const previousDate = row?.date
    ? formatDepositDate(row.date)
    : SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
  const currentDate = draftRow?.date
    ? formatDepositDate(draftRow.date)
    : SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
  const previousAmount = formatMoney(row?.amount)
  const currentAmount = formatMoney(parseDepositAmount(draftRow?.amount))

  if (previousBank !== currentBank) changes.bank = { previous: previousBank, current: currentBank }
  if (previousNumber !== currentNumber)
    changes.number = { previous: previousNumber, current: currentNumber }
  if (normalizeDateInputValue(row?.date) !== normalizeDateInputValue(draftRow?.date))
    changes.date = { previous: previousDate, current: currentDate }
  if (Number(row?.amount || 0) !== parseDepositAmount(draftRow?.amount))
    changes.amount = { previous: previousAmount, current: currentAmount }
  return changes
}

export const getDepositUiChangesForDisplay = (row, localChanges) => {
  const localChange = localChanges[getDepositUiChangeKey(row)]
  if (localChange) return localChange

  const history = row?.edit_history || row?.raw?.edit_history || []
  return history[history.length - 1]?.changes || {}
}

export const getFirstEditedDepositField = (changes) =>
  ['bank', 'number', 'date', 'amount'].find((field) => changes?.[field]) || null

export const normalizeDepositNumber = (value) =>
  String(value ?? '').slice(0, DEPOSIT_NUMBER_MAX_LENGTH)

export const normalizeDepositDraftForSave = (draftRow) => ({
  ...draftRow,
  bank: String(draftRow?.bank ?? '').trim(),
  number: normalizeDepositNumber(String(draftRow?.number ?? '').trim()),
  date: normalizeDateInputValue(draftRow?.date),
  amount: draftRow?.amount
})

export const parseDepositAmount = (value) => {
  const normalizedValue = String(value ?? '')
    .replace(/,/g, '')
    .trim()
  return normalizedValue === '' ? Number.NaN : Number(normalizedValue)
}

export const isDepositDraftFieldInvalid = (field, value) => {
  if (field === 'date') return !normalizeDateInputValue(value)
  if (field === 'amount') {
    const amount = parseDepositAmount(value)
    return value == null || String(value).trim() === '' || !Number.isFinite(amount) || amount <= 0
  }
  return String(value ?? '').trim() === ''
}

export const validateDepositDraft = (draftRow) => {
  const normalizedDraft = normalizeDepositDraftForSave(draftRow)
  return ['bank', 'number', 'date', 'amount'].reduce((errors, field) => {
    if (isDepositDraftFieldInvalid(field, normalizedDraft[field]))
      errors[field] = SETTLEMENT_DETAILS_DEPOSITS_REQUIRED_FIELD_MESSAGE
    return errors
  }, {})
}

export const normalizeDepositAmountInput = (value) => {
  const sanitizedValue = String(value ?? '').replace(/[^\d.]/g, '')
  if (!sanitizedValue) return ''
  const [integerPart = '', ...decimalParts] = sanitizedValue.split('.')
  const normalizedIntegerPart = integerPart.replace(/^0+(?=\d)/, '') || '0'
  const formattedIntegerPart = normalizedIntegerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return sanitizedValue.includes('.')
    ? `${formattedIntegerPart}.${decimalParts.join('').slice(0, 2)}`
    : formattedIntegerPart
}

export const formatDepositAmountForDisplay = (value) => {
  const amount = parseDepositAmount(value)
  return Number.isFinite(amount) && amount > 0
    ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        amount
      )
    : ''
}

export const getDepositAmountExceededMessage = (amountAvailableForDeposit) =>
  `No puede superar ${formatMoney(amountAvailableForDeposit)} (monto pendiente por depositar)`

export const getBankOptionLabel = (bank) => bank?.name || bank?.bank_name || bank?.short_name || ''

export const normalizeDateInputValue = (value) => {
  if (!value) return ''
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) return `${match[1]}-${match[2]}-${match[3]}`
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export const buildDateObjectFromNormalizedValue = (value) => {
  const [year, month, day] = value.split('-').map(Number)
  return year && month && day ? new DateObject({ year, month, day }) : null
}

export const formatSingleDateLabel = (value) => {
  const normalized = normalizeDateInputValue(value)
  if (!normalized) return SETTLEMENT_DETAILS_DEPOSITS_DATE_PLACEHOLDER
  const [year, month, day] = normalized.split('-')
  return `${day}/${month}/${year}`
}

export const formatDepositDate = (value) => {
  const normalized = normalizeDateInputValue(value)
  return normalized ? formatSingleDateLabel(normalized) : SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE
}

export const serializeDepositsForApproval = (rows) =>
  rows.map((row) => {
    const deposit = {
      id: row.id,
      uid: row.uid && row.uid !== '-' ? row.uid : null,
      bank: row.bank && row.bank !== '-' ? row.bank : '',
      number: row.number && row.number !== '-' ? normalizeDepositNumber(row.number) : '',
      date: normalizeDateInputValue(row.date),
      amount: Number(row.amount || 0)
    }
    if (row.bank_uid) deposit.bank_uid = row.bank_uid
    if (row.short_name) deposit.short_name = row.short_name
    return deposit
  })

export const areReceiptRowsEqual = (row, draftRow) =>
  Boolean(row && draftRow) &&
  String(row.uid || '') === String(draftRow.uid || '') &&
  String(row.bank || '') === String(draftRow.bank || '') &&
  normalizeDepositNumber(row.number) === normalizeDepositNumber(draftRow.number) &&
  normalizeDateInputValue(row.date) === normalizeDateInputValue(draftRow.date) &&
  Number(row.amount || 0) === parseDepositAmount(draftRow.amount)
